import { useTranslation } from '../../i18n';
import {
  NumberInput, SearchInput, Select, Textarea, TextInput,
} from '@parsina/framework';
import MoneyInput from './MoneyInput';

/**
 * Thin adapters over the kit's form inputs.
 *
 * These do NOT re-draw anything — every one of them renders the kit component and
 * passes straight through. They exist for two reasons, both of them version facts
 * rather than taste:
 *
 *   1. **`data-testid` does not survive.** Only `MoneyInput` spreads `...rest` in
 *      the installed framework (v0.3.2); the others accept a fixed prop list, so a
 *      testid handed to `Select` or `TextInput` is silently dropped. Since §15.1
 *      makes testids a contract the E2E suite depends on, the id goes on a wrapper
 *      the caller controls.
 *   2. **`Select` takes `options`, not children.** `<Select><option/></Select>`
 *      renders an empty dropdown — no error, no warning, just nothing to pick. That
 *      is a trap worth closing once here instead of at every call site.
 *
 * When the framework grows prop spreading and children-as-options, these collapse
 * to re-exports and the call sites do not change. Until then, this is the layer-2
 * (app-shared) slot in the DESIGN.md sourcing hierarchy: composed FROM kit
 * primitives, never instead of them.
 */

/**
 * Wraps a field so its testid lands on something queryable.
 *
 * The id is on the WRAPPER, not the control, because the kit components it wraps
 * drop unknown props. Address the control as `[data-testid="x"] input` (or
 * `select`/`textarea`) — the `fieldControl` helper below does exactly that, and
 * Playwright selectors follow the same shape.
 */
function Field({ testid, width, flex, children }) {
  return (
    <div data-testid={testid} style={{ width, flex, minWidth: 0 }}>
      {children}
    </div>
  );
}

export function TextField({ testid, width, flex, ...props }) {
  return <Field testid={testid} width={width} flex={flex}><TextInput {...props} /></Field>;
}

export function NumberField({ testid, width, flex, ...props }) {
  return <Field testid={testid} width={width} flex={flex}><NumberInput {...props} /></Field>;
}

export function TextareaField({ testid, width, flex, ...props }) {
  return <Field testid={testid} width={width} flex={flex}><Textarea {...props} /></Field>;
}

/**
 * The kit now dresses `MoneyInput` itself — border, height, focus ring, label,
 * error and helper all come from the same `FormField` its neighbours use, and it
 * carries its own «تومان» chip and Persian numerals. So this is a width wrapper
 * and nothing else; the five class constants that used to re-create TextInput's
 * box here are gone.
 *
 * `unitLabel` passes through for the few amounts that are not toman.
 *
 * `currency` defaults to the clinic's (docs/clinic-currency/CONTRACTS.md): the
 * kit then shows its chip and accepts its fraction digits. The value stays in
 * DISPLAY units either way — the caller converts with `fromDisplayAmount`.
 */
export function MoneyField({ testid, width, flex, currency, ...props }) {
  const { currency: clinicCurrency } = useTranslation();
  return (
    <div style={{ width, flex, minWidth: 0 }}>
      <MoneyInput data-testid={testid} currency={currency || clinicCurrency} {...props} />
    </div>
  );
}

/**
 * `options` as `[{ value, label }]`. `placeholder` becomes the leading empty
 * option; pass `placeholder={null}` for a select that must always hold a value.
 */
export function SelectField({ testid, width, flex, options = [], ...props }) {
  const { t } = useTranslation();
  return (
    <Field testid={testid} width={width} flex={flex}>
      <Select placeholder={t('common.form.chooseEllipsis')} options={options} {...props} />
    </Field>
  );
}

export function SearchField({ testid, width, flex, ...props }) {
  const { t } = useTranslation();
  return <Field testid={testid} width={width} flex={flex}><SearchInput placeholder={t('common.actions.searchPlaceholder')} {...props} /></Field>;
}

/** A standalone label, for the few places a field has none of its own. */
export const LABEL = {
  display: 'block', fontSize: 11.5, fontWeight: 600,
  marginBottom: 4, color: 'var(--p-text-secondary, #5a6780)',
};

/**
 * The control inside a wrapped field, for tests and for focus management.
 *
 * `MoneyField` is the exception: MoneyInput spreads rest, so its testid lands
 * directly on the `<input>` — hence the second branch.
 */
export const fieldControl = (testid, root = document) =>
  root.querySelector(`[data-testid="${testid}"] input, [data-testid="${testid}"] select, [data-testid="${testid}"] textarea`)
  || root.querySelector(`input[data-testid="${testid}"]`);

/** `[{key, labelFa}]` → the shape `Select` wants. Every taxonomy here uses it. */
export const asOptions = (list, { valueKey = 'key', labelKey = 'labelFa' } = {}) =>
  list.map((o) => ({ value: o[valueKey], label: o[labelKey] }));
